/**
 * Indian currency & number formatting helpers.
 *
 * Design: single source of truth for how ₹ amounts render across VAULT.
 * All homepage / dashboard / hub components MUST use these — never format
 * currency inline. Indian digit grouping is 1,25,00,000 (not 12,500,000).
 */

/** Format an amount as ₹1,25,00,000 (or "—" if null/undefined/NaN). */
export function fmtINR(v, { showDecimals = false, showSign = false } = {}) {
    if (v === null || v === undefined || Number.isNaN(v)) return "—";
    const n = Number(v);
    const sign = n < 0 ? "-" : showSign && n > 0 ? "+" : "";
    const abs = Math.abs(n);
    const formatted = abs.toLocaleString("en-IN", {
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
    });
    return `${sign}₹${formatted}`;
}

/** Compact — ₹12.5L, ₹1.25Cr, ₹85K, etc. Good for KPI cards on mobile. */
export function fmtINRCompact(v) {
    if (v === null || v === undefined || Number.isNaN(v)) return "—";
    const n = Number(v);
    const sign = n < 0 ? "-" : "";
    const abs = Math.abs(n);
    if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2).replace(/\.00$/, "")}Cr`;
    if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2).replace(/\.00$/, "")}L`;
    if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
    return `${sign}₹${Math.round(abs).toLocaleString("en-IN")}`;
}

/** Percentage with a sign and one decimal — "+3.2%", "-0.4%", "0%" */
export function fmtPct(v, { decimals = 1 } = {}) {
    if (v === null || v === undefined || Number.isNaN(v)) return "—";
    const n = Number(v);
    if (n === 0) return "0%";
    const sign = n > 0 ? "+" : "";
    return `${sign}${n.toFixed(decimals)}%`;
}

/** Return a delta triple for change tiles. */
export function delta(cur, prev) {
    const c = Number(cur ?? 0);
    const p = Number(prev ?? 0);
    const d = c - p;
    const pct = p === 0 ? (c === 0 ? 0 : 100) : (d / Math.abs(p)) * 100;
    return { delta: d, pct, direction: d > 0 ? "up" : d < 0 ? "down" : "flat" };
}

/** Format a date as "21 Feb 2026". */
export function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

/** Current YYYY-MM string for month filters. */
export function currentMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Human "3 days ago" / "in 2 days" — for last-refresh labels & due dates. */
export function relTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const diff = (d.getTime() - Date.now()) / 1000; // seconds
    const abs = Math.abs(diff);
    const past = diff < 0;
    if (abs < 60) return past ? "just now" : "in a moment";
    if (abs < 3600) {
        const m = Math.round(abs / 60);
        return past ? `${m}m ago` : `in ${m}m`;
    }
    if (abs < 86400) {
        const h = Math.round(abs / 3600);
        return past ? `${h}h ago` : `in ${h}h`;
    }
    const days = Math.round(abs / 86400);
    return past ? `${days}d ago` : `in ${days}d`;
}
